"use client";

import { refresh } from "action/auth/refresh";
import { openModal } from "action/modal/openModal";
import { setCustomerSupportChat } from "action/utils/setCustomerSupportChat";
import AppVersion from "analytics/AppVersion";
import PushEngageScript from "analytics/PushEngageScript";
import { subscribeKlaviyoList } from "analytics/klaviyo";
import KlaviyoScript from "analytics/klaviyo/KlaviyoScript";
import { cleanKlaviyoStorage } from "analytics/klaviyo/klaviyo-utils";
import KlaviyoTrackingService from "analytics/klaviyo/klaviyoEvents";
import Signifyd from "component/Signifyd";
import { setCookie } from "helpers/cookie";
// import StripeContext from "hooks-contexts/StripeContext";
import useMaskIframes from "hooks-contexts/useMaskIframes";
import { RootState } from "interfaces/generics.type";
import measureWebVitals from "modules/lib/web-vitals/pa-web-vitals";
import getStripe from "modules/third-party/stripe/get-stripe";
import { useRouter } from "next/router";
import qs from "query-string";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { activityWatcher } from "util/activitywatcher";
import initLiveChat from "util/chat-widget/init-live-chat";
import { isBusinessHP } from "util/helper/helper";
import isSpecialHoliday from "util/helper/isSpecialHoliday";
import PALocalStorage, {
  getLSItem,
  setGclid,
} from "util/localStorageUtils";
import yieldToMain from "util/lodash/yieldToMain";
import {
  isLoginPage,
} from "util/url/checkRouteInclude";
import { useNextCssRemovalPrevention } from "./useNextCssRemovalPrevention";

const RootLayoutMount = ({ isUnsecure }) => {
  const router = useRouter();
  const { pathname, locale } = router;
  // const pathname = usePathname();
  // const query = useSearchParams();
  const dispatch = useDispatch();
  const { businessCustomer, refreshStatus } = useSelector((store: RootState) => store.authenticationState);
  // useContext(StripeContext);
  // let sentryInitDone = false;

  // https://github.com/vercel/next.js/issues/17464#issuecomment-1537262075
  // CSS is getting removed on client side navigation. This is a bug in NextJS. We need to prevent removal of CSS
  useNextCssRemovalPrevention();

  useMaskIframes();

  const runIdle = (fn: () => void) => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(fn);
    } else {
      setTimeout(fn, 0);
    }
  };

  // use separate useEffect for unrelated logics
  // https://stackoverflow.com/questions/54002792/in-general-is-it-better-to-use-one-or-many-useeffect-hooks-in-a-single-component
  useEffect(() => {
    PALocalStorage.initialize();
    yieldToMain().then(() => {
      measureWebVitals();
    });

    runIdle(() => {
      setCookie("version", process.env.NEXT_PUBLIC_VERSION, 30); // 30 days cache.
      cleanKlaviyoStorage();
      const { gclid } = qs.parse(location.search);
      if (gclid) {
        setGclid(gclid);
      }
    });

    yieldToMain().then(() => {
      activityWatcher();
    });

    // user-session API call
    dispatch(refresh((data) => {
      window.isUserSignedIn = data.transientStateResponse.loggedIn;
    }));

    if (window.location.protocol === "https:") {
      getStripe(locale, isUnsecure);
    }

    const unhandledRejectionHandler = (event:PromiseRejectionEvent) => {
      console.log("Unhandled Promise Rejection", {
        reason: event.reason,
        promise: event.promise,
        stack: event.reason?.stack,
      });
    };

    window.addEventListener("unhandledrejection", unhandledRejectionHandler);

    return () => {
      window.removeEventListener("unhandledrejection", unhandledRejectionHandler);
    };
  }, []);

  // refresh user session on checkout pages, to maintain TransientState, while checkout.
  useEffect(() => {
    const validRefreshPages = [
      "/checkout/s1",
      "/checkout/s2",
      "/checkout/s3",
      "/checkout/s4",
    ];

    yieldToMain().then(() => {
      const userSessionInterval = setInterval(() => {
        const { asPath } = router;
        if (validRefreshPages.includes(asPath)) {
          dispatch(refresh());
        }
      }, 60 * 1000 * 10);

      return () => clearInterval(userSessionInterval);
    });
  }, []);

  // initialize chat widget after 15 seconds
  useEffect(() => {
    if (refreshStatus !== "complete") return;

    const initChat = () => {
      if (
        !isSpecialHoliday
      && !businessCustomer
      && !isBusinessHP(router.asPath)
      && !isLoginPage(pathname)
      && !getLSItem("isBusinessCustomer")
      ) {
        initLiveChat(router.locale);
      }
    };

    // chat initialization time in seconds
    const chatInitializationTime = 15;
    const timeoutId = setTimeout(() => {
      runIdle(initChat);
    }, chatInitializationTime * 1000);

    return () => clearTimeout(timeoutId);
  }, [refreshStatus, businessCustomer]);

  function updateRootFontSizeVar() {
    const rootPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    document.documentElement.style.setProperty("--safari-root-font", `${rootPx }px`);
  }

  // Chat Widget Event listener
  useEffect(() => {
    // Set the type of chat widget to show
    dispatch(setCustomerSupportChat(router.locale === "fr" ? "FRESHDESK" : "PARTSAVATAR_CHAT"));

    const handleWidgetLogin = () => {
      dispatch(openModal("login"));
    };

    const handleSendEmailEvent = (event) => {
    // todo-next-14: locale
      runIdle(() => {
        subscribeKlaviyoList(event.detail, "chat", router.locale);
        KlaviyoTrackingService.identifyKlaviyoUser(event.detail);
      });
    };

    const handleChatWidgetEvent = (event: any) => {
      const { action, ...restEvent } = event.detail;
      switch (action) {
      case "pa-widget-login":
        dispatch(openModal("login"));
        break;
      case "pa-widget-email":
        const { email } = restEvent;
        // todo-next-14: locale
        runIdle(() => {
          subscribeKlaviyoList(email, "chat", router.locale);
          KlaviyoTrackingService.identifyKlaviyoUser(email);
        });
        break;
      case "pa-widget-vehicle-selected":
        runIdle(() => {
          const { vehicle } = restEvent;
          KlaviyoTrackingService.vehicleSelectedEvent(vehicle);
        });
        break;
      case "pa-widget-share-cart-url":
        runIdle(() => {
          const { shareCartUrl, associateName, associatePic } = restEvent;
          KlaviyoTrackingService.shareCartEvent(shareCartUrl, associateName, associatePic);
        });
        break;
      default:
      }
    };

    runIdle(() => {
      document.addEventListener("pa-widget-login", handleWidgetLogin, { passive: true });
      document.addEventListener("pa-widget-email", handleSendEmailEvent, { passive: true });
      if (!isSpecialHoliday) {
        document.addEventListener("chat-widget-listner", handleChatWidgetEvent, { passive: true });
      }
    });

    updateRootFontSizeVar();
    window.addEventListener("resize", updateRootFontSizeVar);

    return () => {
      document.removeEventListener("pa-widget-login", handleWidgetLogin);
      document.removeEventListener("pa-widget-email", handleSendEmailEvent);
      document.removeEventListener("chat-widget-listner", handleChatWidgetEvent);
    };
  }, []);

  return (
    <>
      {/* <NavigationEvents handleRouteChange={googleLoginLoad} /> */}
      {process.env.NODE_ENV !== "development" && <AppVersion />}
      <Signifyd />
      <KlaviyoScript />
      <PushEngageScript />
    </>
  );
};

export default RootLayoutMount;
